import React from 'react';
import { Box, Typography, LinearProgress, Button, SxProps, Theme } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

/** Shared gradient style used across all primary action buttons */
export const gradientButtonSx: SxProps<Theme> = {
  background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
  '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
  color: 'white',
};

// ---------------------------------------------------------------------------
// UploadProgressBar
// ---------------------------------------------------------------------------

interface UploadProgressBarProps {
  uploading: boolean;
  progress: number;
}

export const UploadProgressBar: React.FC<UploadProgressBarProps> = ({ uploading, progress }) => {
  if (!uploading) return null;
  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <LinearProgress variant="determinate" value={progress} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// StatusBanner
// ---------------------------------------------------------------------------

interface StatusBannerProps {
  error: string | null;
  status: string | null;
  sx?: SxProps<Theme>;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({ error, status, sx }) => {
  const base: SxProps<Theme> = { mb: 2, p: 1, borderRadius: 1, whiteSpace: 'pre-wrap', ...sx };

  if (error) {
    return (
      <Box sx={{ ...base, border: '1px solid #ef4444', bgcolor: 'background.paper' }}>
        <Typography variant="body2" color="error">{error}</Typography>
      </Box>
    );
  }
  if (status) {
    return (
      <Box sx={{ ...base, border: '1px solid #55ed44', bgcolor: 'background.paper' }}>
        <Typography variant="body2" color="success.main">{status}</Typography>
      </Box>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// PageHeader — accepts any MUI icon and title
// ---------------------------------------------------------------------------

interface PageHeaderProps {
  title: string;
  Icon: SvgIconComponent;
  onBack: () => void;
  backLabel?: string;
  subtitle?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  Icon,
  onBack,
  backLabel = 'Back',
  subtitle,
}) => (
  <Box sx={{ mb: 3 }}>
    <Button
      variant="text"
      size="small"
      onClick={onBack}
      sx={{ mb: 1, color: 'text.secondary', textTransform: 'none', pl: 0 }}
    >
      ← {backLabel}
    </Button>
    <Typography variant="h4" component="h1"
      sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
      <Icon />
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="h6" sx={{ mt: 1, color: 'text.secondary' }}>{subtitle}</Typography>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// EmptyTableRow — generic empty state
// ---------------------------------------------------------------------------

interface EmptyTableRowProps {
  colSpan: number;
  hasSearch: boolean;
  Icon: SvgIconComponent;
  entityName?: string;
}

export const EmptyTableRow: React.FC<EmptyTableRowProps> = ({
  colSpan,
  hasSearch,
  Icon,
  entityName = 'records',
}) => (
  <tr>
    <td colSpan={colSpan} style={{ textAlign: 'center', padding: '32px 0' }}>
      <Icon style={{ fontSize: 48, opacity: 0.3 }} />
      <Typography variant="h6" color="text.secondary">
        No {entityName} found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {hasSearch ? 'Try adjusting your search terms' : `No ${entityName} have been added yet`}
      </Typography>
    </td>
  </tr>
);